import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { OrgBasicSettingsTab } from '@/components/organization/OrgBasicSettingsTab';
import { OrgPublicSiteSettingsTab } from '@/components/organization/OrgPublicSiteSettingsTab';
import { OrganizationSportsSettingsTab } from '@/components/organization/OrganizationSportsSettingsTab';
import { OrgPlayerRatingSettingsTab } from '@/components/playerRating/OrgPlayerRatingSettingsTab';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { useSubscriptionCheck } from '@/hooks/useSubscriptionCheck';
import { useOrganizationSettingsData } from '@/hooks/useOrganizationSettingsData';
import {
  parseOrganizationTab,
  type OrganizationSettingsTab,
} from '@/lib/settingsTabRoutes';
import { ORGANIZATION_SETTINGS_TOUR_STEP_IDS } from '@/lib/tour-constants';
import {
  ORGANIZATION_SETTINGS_TOUR_KEY,
  scheduleOrganizationSettingsTourStart,
} from '@/lib/tours/organizationSettingsTour';
import { useAutoPageTour } from '@/hooks/useAutoPageTour';
const ORGANIZATION_TABS: {
  id: OrganizationSettingsTab;
  label: string;
  mobileLabel: string;
}[] = [
  { id: 'basic', label: 'Basic information', mobileLabel: 'Basic' },
  { id: 'public-site', label: 'Public site', mobileLabel: 'Public' },
  { id: 'sports', label: 'Sports', mobileLabel: 'Sports' },
  { id: 'player-ratings', label: 'Player ratings', mobileLabel: 'Ratings' },
];

const LEGACY_TAB_ALIASES: Record<string, OrganizationSettingsTab> = {
  customization: 'public-site',
  subdomain: 'public-site',
  'website-embed': 'public-site',
  branding: 'public-site',
};

const ORGANIZATION_SETTINGS_HELP = {
  title: 'Organization settings',
  description:
    'Manage contact details, public site branding, sports offered, and internal player ratings for match play.',
  tourLabel: 'Tour this page',
  tourDescription: 'Walk through all organization settings tabs in one tour.',
};

export default function OrganizationSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasValidSubscription } = useSubscriptionCheck();
  const handleTabChangeRef = useRef<(tab: OrganizationSettingsTab) => void>(() => {});
  const { startTourWithCompletionKey } = usePageTourActions();
  const {
    organization,
    setOrganization,
    clubForTabs,
    subtitle,
    isInitialLoading,
    setClub,
    setCurrentClub,
  } = useOrganizationSettingsData();

  const activeTab = parseOrganizationTab(searchParams.get('tab'));

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && LEGACY_TAB_ALIASES[tabParam]) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', LEGACY_TAB_ALIASES[tabParam]);
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleTabChange = useCallback(
    (tab: OrganizationSettingsTab) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    handleTabChangeRef.current = handleTabChange;
  }, [handleTabChange]);

  const launchOrganizationSettingsTour = useCallback(() => {
    return scheduleOrganizationSettingsTourStart(startTourWithCompletionKey, {
      onEnterBasic: () => handleTabChangeRef.current('basic'),
      onEnterPublicSite: () => handleTabChangeRef.current('public-site'),
      onEnterSports: () => handleTabChangeRef.current('sports'),
      onEnterPlayerRatings: () => handleTabChangeRef.current('player-ratings'),
      onFinish: () => handleTabChangeRef.current('basic'),
    });
  }, [startTourWithCompletionKey]);

  useAutoPageTour(
    ORGANIZATION_SETTINGS_TOUR_KEY,
    Boolean(organization?.id) && !isInitialLoading,
    { onStart: launchOrganizationSettingsTour, afterAnimationMs: 0 },
  );

  if (isInitialLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center" aria-busy="true">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="contents">
        <PageHeader title="Organization" subtitle="Organization not found" />
      </div>
    );
  }

  return (
    <div className="contents">
      <PageHeader
        title="Organization"
        subtitle={subtitle}
        help={
          <PageFeatureHelp
            title={ORGANIZATION_SETTINGS_HELP.title}
            tours={[
              {
                tourKey: ORGANIZATION_SETTINGS_TOUR_KEY,
                label: ORGANIZATION_SETTINGS_HELP.tourLabel,
                description: ORGANIZATION_SETTINGS_HELP.tourDescription,
                onStart: launchOrganizationSettingsTour,
              },
            ]}
          >
            <p>{ORGANIZATION_SETTINGS_HELP.description}</p>
          </PageFeatureHelp>
        }
        helpAnchorId={ORGANIZATION_SETTINGS_TOUR_STEP_IDS.GUIDE}
      />

      <div id={ORGANIZATION_SETTINGS_TOUR_STEP_IDS.TABS}>
        <DashboardTabNav<OrganizationSettingsTab>
          tabs={ORGANIZATION_TABS}
          activeTab={activeTab}
          onChange={handleTabChange}
          ariaLabel="Organization settings sections"
        />
      </div>

      {activeTab === 'basic' ? (
        <div id={ORGANIZATION_SETTINGS_TOUR_STEP_IDS.BASIC}>
          <OrgBasicSettingsTab
            organization={organization}
            club={clubForTabs}
            onOrganizationSaved={setOrganization}
            onClubSaved={(updated) => {
              setClub(updated);
              setCurrentClub(updated);
            }}
          />
        </div>
      ) : null}

      {activeTab === 'public-site' ? (
        <OrgPublicSiteSettingsTab organization={organization} onSaved={setOrganization} />
      ) : null}

      <div className={activeTab === 'sports' ? undefined : 'hidden'} aria-hidden={activeTab !== 'sports'}>
        <OrganizationSportsSettingsTab
          organizationId={organization.id}
          disabled={!hasValidSubscription}
          listTourId={
            activeTab === 'sports' ? ORGANIZATION_SETTINGS_TOUR_STEP_IDS.SPORTS_LIST : undefined
          }
        />
      </div>

      <div
        className={activeTab === 'player-ratings' ? undefined : 'hidden'}
        aria-hidden={activeTab !== 'player-ratings'}
      >
        <OrgPlayerRatingSettingsTab
          organizationId={organization.id}
          formTourId={
            activeTab === 'player-ratings'
              ? ORGANIZATION_SETTINGS_TOUR_STEP_IDS.PLAYER_RATINGS_FORM
              : undefined
          }
        />
      </div>
    </div>
  );
}
